import { forwardRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { IconMagicWand } from 'src/assets/icons/MagicWand';
import { ButtonText } from 'src/components/ButtonText';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ToneOfVoiceCard } from 'src/components/ToneOfVoiceCard';
import { isNonEnglish, sortByLanguage } from 'src/data/generation';
import {
  DRAFT_TOV_ID,
  getPresetTonesOfVoice,
  getToneOfVoiceDescription,
  getToneOfVoiceLabel,
  getToneOfVoiceTitle,
} from 'src/data/toneOfVoice';

import { CustomModelCard } from './component/CustomModelCard';
import { CustomToneOfVoiceCard } from './component/CustomToneOfVoiceCard';
import { NewToneOfVoiceCard } from './component/NewToneOfVoiceCard';
import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  toneOfVoices: PropTypes.arrayOf(PropTypes.shape({})),
  customModels: PropTypes.arrayOf(PropTypes.shape({})),
  onSelect: PropTypes.func.isRequired,
  selectedTov: PropTypes.string,
  selectedCustomModelId: PropTypes.string,
  onAddToneOfVoice: PropTypes.func.isRequired,
  generationTool: PropTypes.string,
  isInfinityMode: PropTypes.bool,
  draftToneOfVoice: PropTypes.shape({}),
  language: PropTypes.string,
  disabled: PropTypes.bool,
};
const defaultProps = {
  className: null,
  toneOfVoices: [],
  customModels: [],
  selectedTov: null,
  selectedCustomModelId: null,
  generationTool: null,
  isInfinityMode: false,
  draftToneOfVoice: null,
  language: null,
  disabled: false,
};

export const ToneOfVoiceList = forwardRef(
  (
    {
      className,
      generationTool,
      toneOfVoices,
      customModels,
      selectedTov,
      selectedCustomModelId,
      draftToneOfVoice,
      onSelect,
      onAddToneOfVoice,
      isInfinityMode,
      language,
      disabled,
    },
    ref
  ) => {
    const styles = useStyles({ isInfinityMode });

    const customItemsExist =
      toneOfVoices.length > 0 || customModels.length > 0 || draftToneOfVoice != null;

    const presetTonesOfVoice = getPresetTonesOfVoice(generationTool);
    const isEnglish = !isNonEnglish(language);

    const handleAddClick = async (event, data) => {
      event.stopPropagation();
      const result = await onAddToneOfVoice(data);
      if (result) {
        onSelect(result);
      }
    };

    const handleCardClick = (event, data) => {
      event.stopPropagation();
      onSelect(data);
    };

    return (
      <div
        className={cx(styles.root, className)}
        ref={ref}
      >
        {customItemsExist && (
          <div className={styles.tovGroupContainer}>
            <div className={styles.header}>
              <CustomTypography
                className={styles.title}
                variant={TYPOGRAPHY_VARIANT.medium16}
              >
                Custom
              </CustomTypography>

              {isEnglish && (
                <ButtonText
                  className={styles.buttonTrain}
                  startIcon={<IconMagicWand className={styles.iconMagicWand} />}
                  onClick={(event) => handleAddClick(event, { shouldOnlyTrain: true })}
                >
                  Train Anyword on your tone of voice
                </ButtonText>
              )}
            </div>

            <div className={styles.tovGroupWrapper}>
              {draftToneOfVoice && (
                <CustomToneOfVoiceCard
                  className={styles.button}
                  toneOfVoice={draftToneOfVoice}
                  onClick={(event) => handleCardClick(event, { draftToneOfVoice })}
                  selected={selectedTov === DRAFT_TOV_ID}
                  disabled={disabled}
                  language={language}
                  onEditDraft={() =>
                    onAddToneOfVoice({ shouldActiveDraftMode: true, draftToneOfVoice })
                  }
                  onSaveDraft={() =>
                    onAddToneOfVoice({ shouldActivateLastStep: true, draftToneOfVoice })
                  }
                />
              )}
              {toneOfVoices
                .sort(sortByLanguage('brandVoiceLanguage', language))
                .map((toneOfVoice) => (
                  <CustomToneOfVoiceCard
                    className={styles.button}
                    key={toneOfVoice.id}
                    toneOfVoice={toneOfVoice}
                    onClick={(event) => handleCardClick(event, { tov: toneOfVoice.id })}
                    selected={toneOfVoice.id === selectedTov}
                    disabled={disabled}
                    language={language}
                  />
                ))}
              {customModels.map((customModel) => (
                <CustomModelCard
                  className={styles.button}
                  key={customModel.id}
                  customModel={customModel}
                  generationTool={generationTool}
                  onClick={(event) => handleCardClick(event, { customModelId: customModel.id })}
                  selected={customModel.id === selectedCustomModelId}
                  disabled={disabled}
                  language={language}
                />
              ))}
            </div>
          </div>
        )}

        <div className={styles.tovGroupContainer}>
          <div className={styles.header}>
            {customItemsExist && (
              <CustomTypography
                className={styles.title}
                variant={TYPOGRAPHY_VARIANT.medium16}
              >
                General
              </CustomTypography>
            )}

            {!customItemsExist && isEnglish && (
              <ButtonText
                className={styles.buttonTrain}
                startIcon={<IconMagicWand className={styles.iconMagicWand} />}
                onClick={(event) => handleAddClick(event, { shouldOnlyTrain: true })}
              >
                Train Anyword on your tone of voice
              </ButtonText>
            )}
          </div>
          <div className={styles.tovGroupWrapper}>
            <NewToneOfVoiceCard
              className={styles.button}
              onClick={(event) => handleAddClick(event, { shouldActiveDraftMode: true })}
            />
            {presetTonesOfVoice.map((presetTov) => (
              <ToneOfVoiceCard
                className={styles.button}
                classes={{
                  description: styles.presetDescription,
                }}
                key={presetTov}
                title={getToneOfVoiceTitle(presetTov, language)}
                description={getToneOfVoiceDescription(presetTov, language)}
                label={getToneOfVoiceLabel(presetTov, language)}
                onClick={(event) => handleCardClick(event, { tov: presetTov })}
                selected={presetTov === selectedTov}
                disabled={disabled}
                presetTov={presetTov}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

ToneOfVoiceList.displayName = 'ToneOfVoiceList';
ToneOfVoiceList.propTypes = propTypes;
ToneOfVoiceList.defaultProps = defaultProps;
